import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type FC,
  type PropsWithChildren,
} from 'react';

import { normalizeRussianLocalPhoneSearchTerm } from '@app/components/Phone/utils';
import type { ClientStatusEnum } from '@app/components/RecordModal/components/RecordDetails/types';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useGetClientsFilterDataQuery,
  useLazySearchClientsQuery,
} from '@app/services/rtkQuery/Clients/api';
import { type ModelClientSearchFilter } from '@app/services/rtkQuery/Clients/types';
import { useErrorHandler } from '@app/shared/hooks';
import type { NClients } from '@app/types';

import {
  getTagsCheckboxOptions,
  getEmployeesOptions,
  getServicesCategoriesOptions,
  getServicesTree,
  formatClientsFilters,
} from '@pages/Clients/NewClientsList/utils';

import { NewClientsListContext } from '@pages/Clients/NewClientsList/context/NewClientsListContext';
import {
  createInitialFilters,
  INITIAL_FILTERS,
  saveFiltersToLocalStorage,
} from '@pages/Clients/NewClientsList/data/constants';
import type {
  FilterType,
  GenderItem,
  NewClientsListFilters,
} from '@pages/Clients/NewClientsList/types';

import type { CheckboxOptionType } from 'antd';
import type dayjs from 'dayjs';
import type { NoUndefinedRangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';

const { Provider } = NewClientsListContext;

export const NewClientsListProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { showNotification } = useNotification();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState<string>(
    sessionStorage.getItem('NewClientsListSearchTerm') || ''
  );
  const [clientsData, setClientsData] = useState<
    NClients.ModelClientSearchResult | undefined
  >();

  const [filters, setFilters] =
    useState<NewClientsListFilters[]>(createInitialFilters);

  const latestRequestIdRef = useRef<string | null>(null);

  const [serverFilter, setServerFilter] = useState<ModelClientSearchFilter>({
    SearchTerm: sessionStorage.getItem('NewClientsListSearchTerm') || '',
    ClientsPerPage: sessionStorage.getItem('NewClientsListClientsPerPage')
      ? Number(sessionStorage.getItem('NewClientsListClientsPerPage'))
      : 20,
    Filters: [],
  });

  useEffect(() => {
    saveFiltersToLocalStorage(filters);
  }, [filters]);

  const {
    data: clientsFilterData,
    isLoading: isClientsFilterDataLoading,
    isFetching: isClientsFilterDataFetching,
    isError: isClientsFilterDataError,
    error: clientsFilterDataError,
    isUninitialized: isClientsFilterDataUninitialized,
    refetch: refetchClientsFilterData,
  } = useGetClientsFilterDataQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  const [
    searchClients,
    { isLoading: isClientsDataLoading, isFetching: isClientsDataFetching },
  ] = useLazySearchClientsQuery();

  useErrorHandler([
    { flag: isClientsFilterDataError, error: clientsFilterDataError },
  ]);

  const searchClientsHandler = useCallback(async () => {
    try {
      const searchTerm = serverFilter.SearchTerm
        ? normalizeRussianLocalPhoneSearchTerm(serverFilter.SearchTerm)
        : serverFilter.SearchTerm;
      const termLength = searchTerm ? searchTerm.length : 0;

      if (termLength > 0 && termLength < 3) return;

      const promise = searchClients({
        ActiveBranchId,
        ...serverFilter,
        SearchTerm: searchTerm,
      });

      latestRequestIdRef.current = promise.requestId;

      const response = await promise.unwrap();

      if (latestRequestIdRef.current !== promise.requestId) return;

      setClientsData(response);
      setPageNumber(response.PageN);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, searchClients, serverFilter, showNotification]);

  useEffect(() => {
    void searchClientsHandler();
  }, [searchClientsHandler]);

  const refetchClientsFilterDataHandler = useCallback(() => {
    refetchClientsFilterData();
  }, [refetchClientsFilterData]);

  const refetchClientsDataHandler = useCallback(
    () => searchClientsHandler(),
    [searchClientsHandler]
  );

  const filtersMap = useMemo(() => {
    const map = new Map<number, NewClientsListFilters>();
    filters.forEach((filter) => {
      map.set(filter.id, filter);
    });
    return map;
  }, [filters]);

  const filtersFormatter = useCallback(() => {
    sessionStorage.setItem('NewClientsListSearchTerm', searchValue || '');
    setServerFilter((prev) => ({
      ...prev,
      SearchTerm: searchValue,
      Filters: formatClientsFilters(filters),
    }));
  }, [filters, searchValue]);

  const onChangeClientsPerPage = useCallback((ClientsPerPage: number) => {
    sessionStorage.setItem(
      'NewClientsListClientsPerPage',
      String(ClientsPerPage)
    );
    setServerFilter((prev) => ({ ...prev, ClientsPerPage }));
  }, []);

  const addNewFilters = useCallback((type: FilterType) => {
    setFilters((prevState) => {
      const newUniqueId =
        prevState.length > 0
          ? Math.max(...prevState.map((filter) => filter.id)) + 1
          : 1;
      return [...prevState, { ...INITIAL_FILTERS, id: newUniqueId, type }];
    });
  }, []);

  const deleteFilterById = useCallback((id: number) => {
    setFilters((prevState) => prevState.filter((filter) => filter.id !== id));
  }, []);

  const onResetVisitsFilter = useCallback(
    (id: number) => {
      const {
        hasRecords,
        recordsStatuses,
        recordsCountRange,
        recordsTags,
        period,
        employees,
        services,
        categories,
        servicesSumRange,
      } = INITIAL_FILTERS;

      const currentFilter = filtersMap.get(id);

      if (!currentFilter) {
        showNotification('Фильтр не найден', 'frontWarning');
      }
      const formattedFilter = {
        ...currentFilter,
        id,
        hasRecords,
        recordsStatuses,
        recordsCountRange,
        recordsTags,
        period,
        employees,
        services,
        categories,
        servicesSumRange,
      };
      setFilters((prevState) =>
        prevState.map((filter) =>
          filter.id === id ? { ...filter, ...formattedFilter } : filter
        )
      );
    },
    [filtersMap, showNotification]
  );

  const onResetClientsFilter = useCallback(
    (id: number) => {
      const {
        genders,
        clientsTags,
        soldRange,
        birthdayPeriod,
        balanceRange,
        ageRange,
        hasTasks,
        hasLoyaltyCard,
      } = INITIAL_FILTERS;

      const currentFilter = filtersMap.get(id);

      if (!currentFilter) {
        showNotification('Фильтр не найден', 'frontWarning');
        return;
      }
      const formattedFilters: NewClientsListFilters = {
        ...currentFilter,
        id,
        genders,
        clientsTags,
        soldRange,
        birthdayPeriod,
        balanceRange,
        ageRange,
        hasTasks,
        hasLoyaltyCard,
      };

      setFilters((prevState) =>
        prevState.map((filter) =>
          filter.id === id ? { ...filter, ...formattedFilters } : filter
        )
      );
    },
    [setFilters, filtersMap, showNotification]
  );

  const onChangeFilterById = useCallback(
    (
      id: number,
      key: keyof NewClientsListFilters,
      value: NewClientsListFilters[keyof NewClientsListFilters]
    ) => {
      const currentFilters = filtersMap.get(id);
      setFilters((prevState) => {
        if (!currentFilters) {
          return prevState;
        }
        const updatedFilters = prevState.map((filter) =>
          filter.id === id ? { ...filter, [key]: value } : filter
        );
        return updatedFilters;
      });
    },
    [filtersMap]
  );

  const onChangeHasRecords = useCallback(
    (hasRecords: boolean | null, id: number) => {
      onChangeFilterById(id, 'hasRecords', hasRecords);
    },
    [onChangeFilterById]
  );

  const onChangeRecordsStatuses = useCallback(
    (recordsStatuses: ClientStatusEnum[], id: number) => {
      onChangeFilterById(id, 'recordsStatuses', recordsStatuses);
    },
    [onChangeFilterById]
  );

  const onChangeRecordsCountRange = useCallback(
    (
      recordsCountRange: { from: number | null; to: number | null },
      id: number
    ) => {
      onChangeFilterById(id, 'recordsCountRange', recordsCountRange);
    },
    [onChangeFilterById]
  );

  const onChangeServicesSumRange = useCallback(
    (
      servicesSumRange: { from: number | null; to: number | null },
      id: number
    ) => {
      onChangeFilterById(id, 'servicesSumRange', servicesSumRange);
    },
    [onChangeFilterById]
  );

  const onChangeRecordsTags = useCallback(
    (recordsTags: number[], id: number) => {
      onChangeFilterById(id, 'recordsTags', recordsTags);
    },
    [onChangeFilterById]
  );

  const onChangeIsIncludeRecordsTags = useCallback(
    (isIncludeRecordsTags: boolean, id: number) => {
      onChangeFilterById(id, 'isIncludeRecordsTags', isIncludeRecordsTags);
    },
    [onChangeFilterById]
  );

  const onChangePeriod = useCallback(
    (period: NoUndefinedRangeValueType<dayjs.Dayjs> | null, id: number) => {
      onChangeFilterById(id, 'period', period);
    },
    [onChangeFilterById]
  );

  const onChangeEmployees = useCallback(
    (employees: number[], id: number) => {
      onChangeFilterById(id, 'employees', employees);
    },
    [onChangeFilterById]
  );

  const onChangeCategories = useCallback(
    (categories: number[], id: number) => {
      onChangeFilterById(id, 'categories', categories);
    },
    [onChangeFilterById]
  );

  const onChangeServices = useCallback(
    (services: string[], id: number) => {
      onChangeFilterById(id, 'services', services);
    },
    [onChangeFilterById]
  );

  const onChangeGenders = useCallback(
    (genders: GenderItem['value'][], id: number) => {
      onChangeFilterById(id, 'genders', genders);
    },
    [onChangeFilterById]
  );

  const onChangeClientsTags = useCallback(
    (clientsTags: number[], id: number) => {
      onChangeFilterById(id, 'clientsTags', clientsTags);
    },
    [onChangeFilterById]
  );

  const onChangeIsIncludeClientsTags = useCallback(
    (isIncludeClientsTags: boolean, id: number) => {
      onChangeFilterById(id, 'isIncludeClientsTags', isIncludeClientsTags);
    },
    [onChangeFilterById]
  );

  const onChangeSoldRange = useCallback(
    (soldRange: { from: number | null; to: number | null }, id: number) => {
      onChangeFilterById(id, 'soldRange', soldRange);
    },
    [onChangeFilterById]
  );

  const onChangeBirthdayPeriod = useCallback(
    (
      birthdayPeriod: NoUndefinedRangeValueType<dayjs.Dayjs> | null,
      id: number
    ) => {
      onChangeFilterById(id, 'birthdayPeriod', birthdayPeriod);
    },
    [onChangeFilterById]
  );

  const onChangeAgeRange = useCallback(
    (ageRange: { from: number | null; to: number | null }, id: number) => {
      onChangeFilterById(id, 'ageRange', ageRange);
    },
    [onChangeFilterById]
  );

  const onChangeBalanceRange = useCallback(
    (balanceRange: { from: number | null; to: number | null }, id: number) => {
      onChangeFilterById(id, 'balanceRange', balanceRange);
    },
    [onChangeFilterById]
  );

  const onChangeSearchValue: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value } }) => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const onChangeFilters = useCallback(
    (newFilters: NewClientsListFilters[]) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const isFiltersLoading = useMemo(
    () =>
      isClientsFilterDataLoading ||
      isClientsFilterDataFetching ||
      isClientsFilterDataUninitialized,
    [
      isClientsFilterDataLoading,
      isClientsFilterDataFetching,
      isClientsFilterDataUninitialized,
    ]
  );

  const isTableLoading = useMemo(
    () => isClientsDataLoading || isClientsDataFetching,
    [isClientsDataLoading, isClientsDataFetching]
  );

  const recordsTagsOptions: CheckboxOptionType<number>[] = useMemo(() => {
    if (!clientsFilterData) {
      return [];
    }

    const { RecordTags } = clientsFilterData;

    const tags = getTagsCheckboxOptions(RecordTags);

    return tags;
  }, [clientsFilterData]);

  const clientsTagsOptions: CheckboxOptionType<number>[] = useMemo(() => {
    if (!clientsFilterData) {
      return [];
    }

    const { ClientTags } = clientsFilterData;

    const tags = getTagsCheckboxOptions(ClientTags);

    return tags;
  }, [clientsFilterData]);

  const activeEmployeesOptions: CheckboxOptionType<number>[] = useMemo(() => {
    if (!clientsFilterData) {
      return [];
    }

    const { Employees } = clientsFilterData;

    const activeEmployees = Employees.filter(({ IsDeleted }) => !IsDeleted);
    const activeEmployeesOptions = getEmployeesOptions(activeEmployees);

    return activeEmployeesOptions;
  }, [clientsFilterData]);

  const firedEmployeesOptions: CheckboxOptionType<number>[] = useMemo(() => {
    if (!clientsFilterData) {
      return [];
    }

    const { Employees } = clientsFilterData;

    const firedEmployees = Employees.filter(({ IsDeleted }) => IsDeleted);
    const firedEmployeesOptions = getEmployeesOptions(firedEmployees);

    return firedEmployeesOptions;
  }, [clientsFilterData]);

  const servicesCategoriesOptions: CheckboxOptionType<number>[] =
    useMemo(() => {
      if (!clientsFilterData) {
        return [];
      }

      const { CategoryServices } = clientsFilterData;

      const servicesCategories = getServicesCategoriesOptions(CategoryServices);

      return servicesCategories;
    }, [clientsFilterData]);

  const getServicesTreeBySearch = useCallback(
    (searchValue: string) => {
      if (!clientsFilterData) {
        return [];
      }

      const { Services, CategoryServices } = clientsFilterData;

      const tree = getServicesTree(Services, CategoryServices, searchValue);

      return tree;
    },
    [clientsFilterData]
  );

  const onSearch = useCallback(() => {
    setServerFilter((prev) => ({
      ...prev,
      SearchTerm: searchValue || null,
      ClientsPerPage: 20,
    }));
  }, [searchValue]);

  const onResetFilters = useCallback(() => {
    setFilters([{ ...INITIAL_FILTERS }]);
    setSearchValue('');
  }, []);

  useEffect(() => {
    filtersFormatter();
  }, [filtersFormatter]);

  return (
    <Provider
      value={{
        filters,
        onChangeHasRecords,
        onChangeRecordsStatuses,
        onChangeRecordsCountRange,
        onChangeServicesSumRange,
        onChangeRecordsTags,
        onChangeIsIncludeRecordsTags,
        onChangePeriod,
        onChangeEmployees,
        onChangeCategories,
        getServicesTreeBySearch,
        onChangeServices,
        onChangeGenders,
        onChangeClientsTags,
        onChangeIsIncludeClientsTags,
        onChangeSoldRange,
        onChangeBirthdayPeriod,
        onChangeAgeRange,
        onChangeBalanceRange,
        addNewFilters,
        deleteFilterById,
        onChangeSearchValue,
        onSearch,
        onResetFilters,
        isFiltersLoading,
        recordsTagsOptions,
        activeEmployeesOptions,
        firedEmployeesOptions,
        servicesCategoriesOptions,
        clientsTagsOptions,
        filtersMap,
        searchValue,
        isTableLoading,
        onChangeClientsPerPage,
        serverFilter,
        clientsData,
        refetchClientsFilterDataHandler,
        refetchClientsDataHandler,
        pageNumber,
        setPageNumber,
        setServerFilter,
        onChangeFilters,
        onResetVisitsFilter,
        onResetClientsFilter,
        setClientsData,
        onChangeFilterById,
      }}
    >
      {children}
    </Provider>
  );
};
